// A tab running a stale build references content-hashed chunk files that stop
// existing after the next deploy. Nothing in the running page can fetch them
// again — the only recovery is a full reload, which revalidates index.html and
// loads the current build.

const CHUNK_RELOAD_AT_KEY = 'chunk-load-error-reload-at';
const CHUNK_RELOAD_MIN_GAP_MS = 60 * 1000;

const CHUNK_LOAD_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i, // Chrome
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /failed to load module script/i,
  /unable to preload css/i, // Vite's CSS preload helper
];

export const isChunkLoadError = (error: unknown): error is Error =>
  error instanceof Error &&
  CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(error.message));

let reloadInitiated = false;

/**
 * Reloads the page to recover from a chunk-load failure. Guarded through
 * sessionStorage so that a reload which does not fix the problem (e.g. mid
 * deploy) cannot loop — at most one automatic reload per
 * CHUNK_RELOAD_MIN_GAP_MS. Returns true when a reload was initiated.
 */
export const reloadOnChunkLoadError = (): boolean => {
  if (reloadInitiated) return true;

  try {
    const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_AT_KEY)) || 0;
    if (Date.now() - lastReloadAt < CHUNK_RELOAD_MIN_GAP_MS) return false;
    sessionStorage.setItem(CHUNK_RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // Without sessionStorage there is no way to break a reload loop — surface
    // the error instead of reloading.
    return false;
  }

  reloadInitiated = true;
  window.location.reload();
  return true;
};
